import { Prisma } from "@prisma/client";
import { BuyableItemEvent, BuyableItemHandler } from "../buyable-item.types";

type PartialPromotionBuyableItem = {
  promotionEvent?: { id: string } | null;
};

type PromotionBuyableItem = {
  promotionEvent: { id: string };
  promotionDates: string[];
};

type PromotionWithEvent = {
  promotionEvent: BuyableItemEvent;
};

const GROUP_KEY_PREFIX = "promotion";

export const PromotionBuyableItemHandler = {
  displayName: "Mise en avant",
  accounting: {
    generalAccountCode: "706502",
    invoiceLabel: "Mise en avant",
  },

  getEvent(item: PromotionWithEvent): BuyableItemEvent {
    return item.promotionEvent;
  },

  isValid(item: PartialPromotionBuyableItem): boolean {
    return !!item.promotionEvent?.id;
  },

  findEquivalent(item: PartialPromotionBuyableItem): Prisma.BuyableItemWhereInput {
    return {
      promotionEvent: {
        id: item.promotionEvent?.id,
      },
    };
  },

  create({
    promotionEvent,
    promotionDates,
  }: PromotionBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      promotionEvent: {
        connect: {
          id: promotionEvent.id,
        },
      },
      promotionDates,
    };
  },

  update({ promotionDates }: { promotionDates: string[] }): Prisma.BuyableItemUpdateInput {
    return {
      promotionDates,
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!PromotionBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for promotion: ${groupKey}`);
    }

    const [, promotionEventId] = groupKey.split("|");
    return {
      promotionEvent: {
        id: promotionEventId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialPromotionBuyableItem): string {
    const eventId = item.promotionEvent?.id;
    if (!eventId) {
      throw new Error("Cannot get group key for invalid promotion item");
    }
    return `${GROUP_KEY_PREFIX}|${eventId}`;
  },

  getPricingQuery(): Prisma.PricingFindFirstArgs {
    return {
      where: {
        backofficePrestation: {
          type: "promotion",
        },
      },
    };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      promotionEvent: { name: { contains: searchText, mode: "insensitive" } },
    };
  },
} as const satisfies BuyableItemHandler;
